/* eslint-disable react/jsx-key */
import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import TopMenu from "../../components/ClockSessions/TopMenu";
import s from "./styles";
import useClockSessions from "../../graphql/useClockSessions";
import { useBusinessContext } from "../../BusinessApp";
import useUsers from "graphql/useUsers";
import useStores from "graphql/useStores";
import WorkerDetails from "components/ClockSessions/WorkerDetails/WorkerDetails";
import GeneralTable from "./GeneralTable";
import Loading from "components/common/loading";
import { utils, writeFile } from "xlsx";
import { getDateHeader } from "helpers/dataHelper";
import { printDocumentPDF } from "../../helpers/utils";

function ClockSessions() {
  const { t } = useTranslation();

  const { users, loading: loading3 } = useUsers();
  const { stores, loading: loading4 } = useStores();

  const mappedUsers = useMemo(() => {
    const result = {};
    users.forEach((user) => {
      result[user._id] = { ...user, fullName: `${user.name} ${user.lastName}` };
    });
    return result;
  }, [users]);

  const { storeId } = useBusinessContext();

  const [selectStore, setSelectStore] = useState(
    () => storeId || (stores.length > 0 && stores[0]._id)
  );

  useEffect(() => {
    if (stores.length > 0) {
      setSelectStore(stores[0]?._id);
    }
  }, [stores]);

  const [selectedWorker, setSelectedWorker] = useState(null);

  const [date, setDate] = useState({
    value: moment(),
    periodType: "date",
    range: { start: moment().startOf("day"), end: moment().endOf("day") },
  });

  const { loading: loading1, clockSessions } = useClockSessions(
    selectStore,
    date.range.start,
    date.range.end
  );

  const workersOptions = useMemo(() => {
    const allOption = { name: t("containers.ClockSessions.all"), value: null };
    const userOptions = users.map((user) => ({
      name: `${user.name} ${user.lastName}`,
      value: user._id,
    }));
    return [allOption, ...userOptions];
  }, [users]);

  const storesOptions = useMemo(() => {
    return stores.map((store) => ({
      name: store.address1,
      value: store._id,
    }));
  }, [stores]);

  const dataToDownload = useMemo(() => {
    return clockSessions
      .filter((clockSession) => {
        if (selectedWorker) {
          return clockSession.userId === selectedWorker;
        }
        return true;
      })
      .map((clockSession) => {
        return [
          mappedUsers[clockSession.userId]?.fullName,
          t(`containers.ClockSessions.${clockSession.type}`),
          moment(clockSession.sessionStart).isValid()
            ? moment(clockSession.sessionStart).format("lll")
            : "-",
          moment(clockSession.sessionEnd).isValid()
            ? moment(clockSession.sessionEnd).format("lll")
            : "-",
        ];
      });
  }, [clockSessions, selectedWorker]);

  const exportFileExcel = useCallback(() => {
    const worksheetData = [
      [
        t("containers.ClockSessions.worker"),
        t("containers.ClockSessions.type"),
        t("containers.ClockSessions.start"),
        t("containers.ClockSessions.end"),
      ],
      ...dataToDownload,
    ];

    const workbook = utils.book_new();
    const sheetName = t("containers.ClockSessions.timekeeping");
    const worksheet = utils.aoa_to_sheet(worksheetData);
    utils.book_append_sheet(workbook, worksheet, sheetName);

    writeFile(workbook, `${t("containers.ClockSessions.timekeeping")}.xlsx`);
  }, [date.range.start, date.range.end, dataToDownload]);

  const getImageGraph = useCallback(async () => {
    await printDocumentPDF(
      {
        dateHeader: getDateHeader(date.range.start, date.range.end)[0],
        dataToDownload: dataToDownload,
      },
      `${t("containers.ClockSessions.timekeeping")}.pdf`
    );
  }, [date, dataToDownload, t]);

  const loadingAll = loading1 || loading3 || loading4;

  return (
    <s.PageContainer>
      <TopMenu
        value={date.value}
        onChange={(value, periodType, range) => {
          setDate({ value, periodType, range });
        }}
        selectedWorker={selectedWorker}
        changeSelectedWorker={(v) => setSelectedWorker(v)}
        selectStore={selectStore}
        setSelectStore={setSelectStore}
        workersOptions={workersOptions}
        exportFileExcel={exportFileExcel}
        getImageGraph={getImageGraph}
        loading={loadingAll}
        storesOptions={storesOptions}
      />

      {loadingAll ? (
        <Loading />
      ) : (
        <>
          {selectedWorker && (
            <WorkerDetails
              loadingAll={loadingAll}
              clockSessions={clockSessions}
              selectedWorker={selectedWorker}
              worker={mappedUsers[selectedWorker]}
            />
          )}
          {!selectedWorker && (
            <GeneralTable
              clockSessions={clockSessions}
              mappedUsers={mappedUsers}
              loading={loadingAll}
            />
          )}
        </>
      )}
    </s.PageContainer>
  );
}

export default ClockSessions;
